import React, { useState, useEffect } from 'react'
import { StyleSheet, View } from 'react-native'
import { IconButton, Text, Button } from 'react-native-paper'
import { StackScreenProps } from '@react-navigation/stack'
import { AppNavigatorParams } from '../../../navigation/types'
import { useTranslation } from 'react-i18next'
import { ScreenWrapper } from '../../../@ui/containers/grid/screen.wrapper'
import MelddTabs from '../../../@ui/buttons/meldd.tabs'
import { Row } from '../../../@ui'
import { MyCollaboratorsListTab } from './tabs/my-collaborators-list.tab'
import { MyCollaboratorsUpdatesTab } from './tabs/my-collaborators-events.tab'
import { CreateCollaboratorListModal } from './components/create-collaborator-list.modal'
import { ViewCollaboratorListsModal } from './components/view-collaborator-lists.modal'

type Props = StackScreenProps<AppNavigatorParams, 'MyCollaborators'>

type CollaboratorsTab = 'updates' | 'list'

export const MyCollaboratorsScreen: React.FC<Props> = ({
  route,
  navigation,
}) => {
  const { t } = useTranslation('nodes')
  const tabParam = route.params?.tab as string | undefined
  const toValidTab = (t: string | undefined): CollaboratorsTab =>
    t === 'updates' || t === 'list' ? t : 'updates'
  const [selectedTab, setSelectedTab] = useState<CollaboratorsTab>(() =>
    toValidTab(tabParam)
  )
  const [createListModalVisible, setCreateListModalVisible] = useState(false)
  const [viewListsModalVisible, setViewListsModalVisible] = useState(false)

  useEffect(() => {
    setSelectedTab(toValidTab(tabParam))
  }, [tabParam])

  const tabItems = [
    {
      icon: 'bell-outline',
      label: t('collaborators-updates') || 'Updates',
      value: 'updates',
      badge: 0,
    },
    {
      icon: 'account-group-outline',
      label: t('collaborators-list') || 'Collaborators',
      value: 'list',
      badge: 0,
    },
  ]

  const handleTabSelect = (value: string) => {
    const tab = value as CollaboratorsTab
    setSelectedTab(tab)
    navigation.setParams({ tab })
  }

  return (
    <ScreenWrapper withScrollView={false}>
      <View style={styles.headerSection}>
        <Row
          alignItems="center"
          justifyContent="space-between"
          style={styles.headerRow}
        >
          <IconButton
            mode="outlined"
            icon="arrow-left"
            onPress={() => navigation.goBack()}
          />
          <Text variant="headlineSmall" style={styles.title}>
            {t('menuItems:my-collaborators-page')}
          </Text>
          {selectedTab === 'list' ? (
            <IconButton
              icon="tag-outline"
              mode="outlined"
              onPress={() => setViewListsModalVisible(true)}
              size={20}
            />
          ) : (
            <View style={styles.placeholder} />
          )}
        </Row>
        <View style={styles.tabsContainer}>
          <MelddTabs
            items={tabItems}
            selected={selectedTab}
            onSelect={handleTabSelect}
          />
        </View>
      </View>

      {selectedTab === 'updates' && <MyCollaboratorsUpdatesTab />}
      {selectedTab === 'list' && <MyCollaboratorsListTab />}

      <CreateCollaboratorListModal
        visible={createListModalVisible}
        onDismiss={() => setCreateListModalVisible(false)}
      />
      <ViewCollaboratorListsModal
        visible={viewListsModalVisible}
        onDismiss={() => setViewListsModalVisible(false)}
        onOpenCreateList={() => {
          setViewListsModalVisible(false)
          setCreateListModalVisible(true)
        }}
      />
    </ScreenWrapper>
  )
}

const styles = StyleSheet.create({
  headerSection: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
  },
  headerRow: {
    width: '100%',
  },
  title: {
    fontWeight: '600',
    textAlign: 'center',
    flex: 1,
  },
  tabsContainer: {
    marginTop: 8,
  },
  placeholder: {
    width: 48,
  },
})

export default MyCollaboratorsScreen
